/**
 * Analytic event helper functions
 *
 * @packageDocumentation
 */

import { CUSTOM_EVENT_TYPE } from 'constants/types'
import { logger } from 'utils/system/logger'

/**
 * Dispatch `form_submission` Analytics Custom Event with `form_name` and optional `input_text` custom event attributes.
 * NOTE:
 *  1. Personal Identifiable Information (PII) should NOT be attached as `input_text` value.
 *  2. Only one input element value can be set as `input_text` optional event attribute from the given form.
 * @param {string} formName Form name
 * @param {HTMLInputElement} targetFormInputElement  HTML input text element
 * @param {boolean} inputElementValueAttached Whether input text element's value need to be attached as event attribute for the custom event or not. PII value should NOT be attached (`false`).
 * @returns {void}
 */
export const dispatchAnalyticsFormSubmissionEvent = (
    formName: string,
    targetFormInputElement: HTMLInputElement,
    inputElementValueAttached: boolean
): void => {
    if (!(targetFormInputElement && targetFormInputElement.value)) return

    const { mparticleInputTextKey, inputTextValue } = getInputTextKeyValue(
        targetFormInputElement,
        inputElementValueAttached
    )

    const detail = {
        event_name: 'form_submission',
        control_fields: {
            mparticle: {
                keys: {
                    form_name: 'form_name',
                    ...mparticleInputTextKey
                },
                mp_event_type: 'Other'
            },
            permutive: {
                keys: {
                    form_name: 'form_name'
                }
            }
        },
        form_name: formName,
        ...inputTextValue
    }
    //dispatch Analytic Event
    document.dispatchEvent(new CustomEvent(CUSTOM_EVENT_TYPE, { detail }))
}

/**
 * Build mParticle event attribute key with input element value if inputElementValueAttached is true.
 * @param {HTMLInputElement} targetFormInputElement
 * @param {boolean} inputElementValueAttached
 * @returns {Record<string, Record<string, string>>}
 */
const getInputTextKeyValue = (
    targetFormInputElement: HTMLInputElement,
    inputElementValueAttached: boolean
): Record<string, Record<string, string>> => {
    let inputTextValue = {}
    let mparticleInputTextKey = {}
    if (inputElementValueAttached) {
        inputTextValue = { input_text: targetFormInputElement.value }
        mparticleInputTextKey = { input_text: 'input_text' }
    }
    return { mparticleInputTextKey, inputTextValue }
}

/**
 * Dispatch `find_vehicle_submit` Analytics Custom Event
 * @param {Array<string>} makes makes selected
 * @param {Array<string>} models models selected
 * @param {Array<string>} bodystyles bodystyles selected
 * @param {string} minPrice min Price selected
 * @param {string} maxPrice max Price selected
 * @param {string} sort sort order selected
 * @returns {void}
 */
export const dispatchAnalyticsFindVehicleSubmitEvent = (
    makes: Array<string>,
    models: Array<string>,
    bodystyles: Array<string>,
    minPrice: string,
    maxPrice: string,
    years: Array<string>,
    seats: Array<string>,
    powerSources: Array<string>,
    transmissions: Array<string>,
    sort: string
): void => {
    try {
        const mparticleKeys: Record<string, string> = {
            filter_type: 'filter_type'
        }
        const mparticleValues: Record<string, string> = {
            filter_type: 'Standard'
        }
        const permutiveKeys: Record<string, string> = {}

        if (makes.length) {
            mparticleKeys['make'] = 'make'
            permutiveKeys['make'] = 'car_make'
            mparticleValues['make'] = makes.join(',')
        }
        if (models.length) {
            mparticleKeys['model'] = 'model'
            permutiveKeys['model'] = 'car_model'
            mparticleValues['model'] = models.join(',')
        }
        if (!models.length || models.length > 1) {
            if (bodystyles.length) {
                mparticleKeys['body_style'] = 'body_style'
                permutiveKeys['body_style'] = 'car_bodystyle'
                mparticleValues['body_style'] = bodystyles.join(',')
            }
            if (minPrice) {
                mparticleKeys['min_price'] = 'min_price'
                permutiveKeys['min_price'] = 'min_price'
                mparticleValues['min_price'] = minPrice
            }
            if (maxPrice) {
                mparticleKeys['max_price'] = 'max_price'
                permutiveKeys['max_price'] = 'max_price'
                mparticleValues['max_price'] = maxPrice
            }
        }
        if (years.length) {
            mparticleKeys['years'] = 'years'
            mparticleValues['years'] = years.join(',')
        }
        if (seats.length) {
            mparticleKeys['seats'] = 'seats'
            mparticleValues['seats'] = seats.join(',')
        }
        if (powerSources.length) {
            mparticleKeys['power_sources'] = 'power_sources'
            mparticleValues['power_sources'] = powerSources.join(',')
        }
        if (transmissions.length) {
            mparticleKeys['transmissions'] = 'transmissions'
            mparticleValues['transmissions'] = transmissions.join(',')
        }
        mparticleKeys['sort'] = 'sort'
        mparticleValues['sort'] = sort || 'popular'

        const detail = {
            event_name: 'find_vehicle_submit',
            control_fields: {
                mparticle: {
                    keys: mparticleKeys,
                    mp_event_type: 'Other'
                },
                permutive: {
                    keys: permutiveKeys
                }
            },
            ...mparticleValues
        }
        //dispatch Analytic Event
        document.dispatchEvent(new CustomEvent(CUSTOM_EVENT_TYPE, { detail }))
    } catch (error) {
        logger('error', error)
    }
}

/**
 * Dispatch `click_to_section` Analytics Custom Event
 * @param {string} sourceSection Source section of event
 * @param {string} targetName target section of click
 * @returns {void}
 */
export const dispatchAnalyticsClickToSectionEvent = (
    sourceSection: string,
    targetName: string
): void => {
    try {
        const detail = {
            event_name: 'click_to_section',
            control_fields: {
                mparticle: {
                    keys: {
                        source_section: 'source_section',
                        target_name: 'target_name'
                    }
                }
            },
            source_section: sourceSection,
            target_name: targetName
        }
        //dispatch Analytic Event
        document.dispatchEvent(new CustomEvent(CUSTOM_EVENT_TYPE, { detail }))
    } catch (error) {
        logger('error', error)
    }
}

/**
 * Dispatch `submission_error` Analytics Custom Event
 * @param {string} errorMessage
 * @param {string} formName
 * @param {string} inputId
 * @returns {void}
 */
export const dispatchAnalyticsSubmissionErrorEvent = (
    errorMessage: string,
    formName: string,
    inputId: string
) => {
    try {
        const detail = {
            event_name: 'submission_error',
            control_fields: {
                mparticle: {
                    keys: {
                        error_message: 'error_message',
                        form_name: 'form_name',
                        input_id: 'input_id'
                    },
                    mp_event_type: 'UserContent'
                }
            },
            error_message: errorMessage,
            form_name: formName,
            input_id: inputId
        }

        //dispatch Analytic Event
        document.dispatchEvent(new CustomEvent(CUSTOM_EVENT_TYPE, { detail }))
    } catch (error) {
        logger('error', error)
    }
}
